import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, UserPlus, KeyRound, Trash2, EyeOff, Eye, Mail } from "lucide-react";
import {
  listarUsuariosCliente,
  criarUsuarioCliente,
  atualizarUsuarioCliente,
  excluirUsuarioCliente,
  UsuarioCliente,
} from "../../services/clientes";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";

interface Props {
  clienteId: string;
  clienteNome: string;
  onClose: () => void;
}

export function UsuariosClienteModal({ clienteId, clienteNome, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  const ehAdmin = can(["admin"]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState<UsuarioCliente | null>(null);
  const [resetando, setResetando] = useState<UsuarioCliente | null>(null);
  const [erroForm, setErroForm] = useState("");

  const { data: usuarios = [], isLoading } = useQuery({
    queryKey: ["cliente-usuarios", clienteId],
    queryFn: () => listarUsuariosCliente(clienteId),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["cliente-usuarios", clienteId] });
    qc.invalidateQueries({ queryKey: ["clientes"] });
  }

  const criarM = useMutation({
    mutationFn: (dados: { nome: string; email: string; senha: string }) => criarUsuarioCliente(clienteId, dados),
    onSuccess: () => { setMostrarForm(false); setErroForm(""); invalidate(); },
    onError: (e: any) => setErroForm(e?.response?.data?.error?.message || "Erro ao criar usuário"),
  });

  const updateM = useMutation({
    mutationFn: ({ userId, dados }: { userId: string; dados: any }) =>
      atualizarUsuarioCliente(clienteId, userId, dados),
    onSuccess: () => { setEditando(null); setResetando(null); setErroForm(""); invalidate(); },
    onError: (e: any) => setErroForm(e?.response?.data?.error?.message || "Erro ao atualizar"),
  });

  const excluirM = useMutation({
    mutationFn: (userId: string) => excluirUsuarioCliente(clienteId, userId),
    onSuccess: () => invalidate(),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800">Usuários do cliente</h3>
            <p className="text-xs text-slate-500">{clienteNome}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {isLoading ? (
            <p className="text-sm text-slate-400 text-center py-4">Carregando…</p>
          ) : usuarios.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nenhum acesso criado ainda.</p>
          ) : (
            <div className="space-y-2">
              {usuarios.map((u) => (
                <div key={u.id} className={`border rounded-lg p-3 ${u.ativo ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`}>
                  {editando?.id === u.id ? (
                    <FormUsuario
                      inicial={u}
                      onCancel={() => { setEditando(null); setErroForm(""); }}
                      onSave={(dados) => updateM.mutate({ userId: u.id, dados })}
                      loading={updateM.isPending}
                      erro={erroForm}
                    />
                  ) : resetando?.id === u.id ? (
                    <ResetSenhaForm
                      onCancel={() => { setResetando(null); setErroForm(""); }}
                      onSave={(senha) => updateM.mutate({ userId: u.id, dados: { senha } })}
                      loading={updateM.isPending}
                      erro={erroForm}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{u.nome}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 truncate">
                          <Mail size={10} /> {u.email}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${u.ativo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                        {u.ativo ? "Ativo" : "Inativo"}
                      </span>
                      <button onClick={() => setEditando(u)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Editar nome/e-mail">
                        <Mail size={14} />
                      </button>
                      <button onClick={() => setResetando(u)} className="p-1.5 text-slate-400 hover:text-amber-600" title="Resetar senha">
                        <KeyRound size={14} />
                      </button>
                      <button
                        onClick={() => updateM.mutate({ userId: u.id, dados: { ativo: !u.ativo } })}
                        className="p-1.5 text-slate-400 hover:text-slate-700"
                        title={u.ativo ? "Desativar" : "Reativar"}
                      >
                        {u.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      {ehAdmin && (
                        <button
                          onClick={async () => {
                            const ok = await confirm({ title: "Excluir usuário", message: `Excluir definitivamente o acesso "${u.nome}"?`, danger: true, confirmText: "Excluir" });
                            if (ok) excluirM.mutate(u.id);
                          }}
                          className="p-1.5 text-slate-400 hover:text-red-600"
                          title="Excluir permanentemente"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {mostrarForm ? (
            <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">Novo acesso</p>
              <FormUsuario
                onCancel={() => { setMostrarForm(false); setErroForm(""); }}
                onSave={(dados) => {
                  if (!dados.senha) { setErroForm("Senha obrigatória"); return; }
                  criarM.mutate({ nome: dados.nome, email: dados.email, senha: dados.senha });
                }}
                loading={criarM.isPending}
                erro={erroForm}
                exigeSenha
              />
            </div>
          ) : (
            <button
              onClick={() => { setMostrarForm(true); setErroForm(""); }}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              <UserPlus size={14} /> Novo acesso
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormUsuario({
  inicial,
  onCancel,
  onSave,
  loading,
  erro,
  exigeSenha,
}: {
  inicial?: UsuarioCliente;
  onCancel: () => void;
  onSave: (dados: { nome: string; email: string; senha?: string }) => void;
  loading: boolean;
  erro: string;
  exigeSenha?: boolean;
}) {
  const [nome, setNome] = useState(inicial?.nome || "");
  const [email, setEmail] = useState(inicial?.email || "");
  const [senha, setSenha] = useState("");

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave({ nome, email, senha: senha || undefined }); }}
      className="space-y-2"
    >
      <input
        type="text"
        required
        placeholder="Nome"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
      />
      <input
        type="email"
        required
        placeholder="E-mail"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
      />
      {exigeSenha && (
        <input
          type="password"
          required
          minLength={6}
          placeholder="Senha inicial (mín. 6)"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
        />
      )}
      {erro && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{erro}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-xs bg-slate-100 hover:bg-slate-200">Cancelar</button>
        <button type="submit" disabled={loading} className="px-3 py-1.5 rounded text-xs bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {loading ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </form>
  );
}

function ResetSenhaForm({ onCancel, onSave, loading, erro }: {
  onCancel: () => void;
  onSave: (senha: string) => void;
  loading: boolean;
  erro: string;
}) {
  const [senha, setSenha] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (senha.length >= 6) onSave(senha); }}
      className="space-y-2"
    >
      <p className="text-xs font-semibold text-slate-700">Resetar senha</p>
      <input
        type="password"
        required
        minLength={6}
        placeholder="Nova senha (mín. 6)"
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-amber-500"
      />
      {erro && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{erro}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-xs bg-slate-100 hover:bg-slate-200">Cancelar</button>
        <button type="submit" disabled={loading || senha.length < 6} className="px-3 py-1.5 rounded text-xs bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50">
          {loading ? "Salvando…" : "Resetar senha"}
        </button>
      </div>
    </form>
  );
}
